import { AuthUtils } from '@/utils/auth';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { Capacitor } from '@capacitor/core';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar, Style } from '@capacitor/status-bar';
import LiveUpdateService from './services/LiveUpdateService';
import { IonApp, IonButton, IonCard, IonCardContent, IonCol, IonContent, IonGrid, IonIcon, IonPage, IonRow, setupIonicReact, useIonRouter } from '@ionic/react';
import { IonReactRouter } from '@ionic/react-router';
import * as Sentry from '@sentry/react';
import { refreshOutline } from 'ionicons/icons';
import React, { useEffect, useState } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import { clearSentryUser, setSentryUser } from './utils/sentry';

import { DeepLinkProvider } from './context/DeepLinkContext';
import { NotificationProvider } from './context/NotificationContext';
import { DebugModeProvider, UserProfileProvider, useUserProfile } from './context/UserProfileContext';
import GameResultPage from './pages/GameResultPage';
import Landing from './pages/Landing';
import NotFound from './pages/NotFound';

/* Core CSS required for Ionic components to work properly */
import '@ionic/react/css/core.css';

/* Basic CSS for apps built with Ionic */
import '@ionic/react/css/normalize.css';
import '@ionic/react/css/structure.css';
import '@ionic/react/css/typography.css';

/* Optional CSS utils that can be commented out */
import '@ionic/react/css/display.css';
import '@ionic/react/css/flex-utils.css';
import '@ionic/react/css/float-elements.css';
import '@ionic/react/css/padding.css';
import '@ionic/react/css/text-alignment.css';
import '@ionic/react/css/text-transformation.css';

import { useTranslation } from 'react-i18next';
import { DebugBanner } from './components/DebugBanner';
import GolfLoading from './components/GolfLoading';
import { MainSidebar } from './components/MainSidebar';
import MobileNav from './components/MobileNav';
import { CreatePostModal } from './components/post/CreatePostModal';
import CustomSignIn from './pages/CustomSignIn';
import CustomSignUp from './pages/CustomSignUp';
import ForgotPassword from './pages/ForgotPassword';
import Mobile from './pages/mobile/Mobile';
import Welcome from './pages/Welcome';
import { postEventService } from './services/PostEventService';
import { shareIntentService } from './services/ShareIntentService';
import { applyTheme, getInitialTheme } from './utils/theme';

setupIonicReact({
  mode: 'ios',
  backButtonText: ''
});

const AuthenticatedApp: React.FC = () => {
  const { profile, isProfileLoading, refreshProfile } = useUserProfile();
  const [isSignedIn, setIsSignedIn] = useState<boolean | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [initError, setInitError] = useState<{ message: string, details: string } | null>(null);
  const [initAttempts, setInitAttempts] = useState(0);
  const [authInitialized, setAuthInitialized] = useState(false);
  const [sharedMedia, setSharedMedia] = useState<any[]>([]);
  const [showCreatePostModal, setShowCreatePostModal] = useState(false);

  const { t } = useTranslation();
  // Listen for shared media from other apps and post creation events
  useEffect(() => {
    // Subscribe to shared media events (images/videos)
    const mediaSubscription = shareIntentService.sharedMedia$.subscribe(media => {

      if (media && media.length > 0) {
        setSharedMedia(media);

        // Only show the create post modal if the user is signed in and has a profile
        if (isSignedIn && profile) {
          alert('🔵 App.tsx: User is signed in with profile, showing create post modal');
          setShowCreatePostModal(true);
        }
      }
    });

    // Listen for openCreatePostModal events from anywhere in the app
    const openModalListener = (data: any) => {
      if (isSignedIn && profile) {
        setShowCreatePostModal(true);
        if (data && data.sharedMedia) {
          setSharedMedia(data.sharedMedia);
        } else {
          setSharedMedia([]);
        }
      } else {
        alert(t('sign_in_to_post'));
      }
    };

    postEventService.addEventListener('openCreatePostModal', openModalListener);

    return () => {
      mediaSubscription.unsubscribe();
      postEventService.removeEventListener('openCreatePostModal', openModalListener);
    };
  }, [isSignedIn, profile, t]);

  // Initialize theme and language
  useEffect(() => {
    const initializeLanguageAndTheme = async () => {
      await FirebaseAuthentication.useAppLanguage();
      await FirebaseAuthentication.setLanguageCode({ languageCode: 'fr-FR' });
      applyTheme(getInitialTheme());
    };

    initializeLanguageAndTheme();
  }, []);

  // Check for app updates when the app starts
  useEffect(() => {
    const checkForUpdates = async () => {
      if (Capacitor.isNativePlatform()) {
        try {
          // Wait a bit for the app to fully initialize before checking for updates
          setTimeout(async () => {
            await LiveUpdateService.checkAndApplyUpdate();
          }, 3000);
        } catch (error) {
          console.error('Error checking for updates:', error);
        }
      }
    };

    checkForUpdates();
  }, []);

  // Initialize auth state and listen for changes
  useEffect(() => {
    const initializeAuth = async () => {
      // First check environment
      const currentEnv = process.env.NODE_ENV;
      const lastUsedEnv = localStorage.getItem('lastUsedEnv');

      if (lastUsedEnv && lastUsedEnv !== currentEnv) {
        console.warn(`Environment changed from ${lastUsedEnv} to ${currentEnv}, forcing logout`);
        await FirebaseAuthentication.signOut();
        localStorage.clear();
        window.location.reload();
        return;
      }

      localStorage.setItem('lastUsedEnv', currentEnv || '');

      // First check if user is already signed in - same approach as DebugBanner.tsx
      const result = await FirebaseAuthentication.getCurrentUser();
      const isUserSignedIn = !!result.user;
      setIsSignedIn(isUserSignedIn);

      // Update Sentry user information if signed in
      if (isUserSignedIn && result.user) {
        setSentryUser(
          result.user.uid,
          result.user.email || undefined,
          result.user.displayName || undefined
        );
      }

      // Set up listener for auth state changes - same approach as DebugBanner.tsx
      FirebaseAuthentication.addListener('authStateChange', async (event) => {
        const isUserSignedIn = !!event.user;
        setIsSignedIn(isUserSignedIn);

        // Update Sentry user information
        if (isUserSignedIn && event.user) {
          setSentryUser(
            event.user.uid,
            event.user.email || undefined,
            event.user.displayName || undefined
          );
        } else {
          clearSentryUser();
        }
      });

      // Add direct listener for sign-out event
      const handleUserSignout = () => {
        console.log('User signout event received in App.tsx');
        setIsSignedIn(false);
        clearSentryUser();
      };

      window.addEventListener('user-signout', handleUserSignout);

      setAuthInitialized(true);

      // Return cleanup function
      return () => {
        window.removeEventListener('user-signout', handleUserSignout);
      };

    };

    // Initialize auth and store the cleanup function
    const cleanupFn = initializeAuth();

    return () => {
      // Clean up auth listeners
      AuthUtils.removeAllListeners();
      // Call the cleanup function from initializeAuth if it exists
      cleanupFn?.then(fn => fn?.());
    };
  }, []);

  // Handle native UI elements and ensure splash screen is properly hidden
  useEffect(() => {
    const setupNativeUI = async () => {
      try {
        // Set up status bar
        if (Capacitor.isPluginAvailable('StatusBar')) {
          await StatusBar.show();
          await StatusBar.setStyle({ style: Style.Default });
        }

        // We'll keep the splash screen visible until we know the app is ready
        // or until we detect an error
      } catch (error) {
        console.error('Error setting up native UI:', error);
        setInitError({
          message: 'Error initializing app UI',
          details: error instanceof Error ? error.message : String(error)
        });

        // Hide splash screen to show error message
        if (Capacitor.isPluginAvailable('SplashScreen')) {
          console.log('Hiding splash screen due to UI error');
          SplashScreen.hide()
            .catch(err => console.error('Error hiding splash screen:', err));
        }
      }
    };

    setupNativeUI();
  }, []);

  // Control loading state and handle network errors
  useEffect(() => {
    // Set a timeout to detect if profile loading is taking too long
    const networkTimeoutId = setTimeout(() => {
      if (isProfileLoading && initAttempts < 3) {
        console.error('Profile loading timeout - possible network issue');
        setInitError({
          message: 'Network connection issue',
          details: `Unable to connect to server. Check your internet connection. (Attempt ${initAttempts + 1}/3)`
        });

        // Hide splash screen to show error message
        if (Capacitor.isPluginAvailable('SplashScreen')) {
          console.log('Hiding splash screen due to UI error');
          SplashScreen.hide()
            .catch(err => console.error('Error hiding splash screen:', err));
        }
      }
    }, 10000); // 10 second timeout

    // When profile loading completes (success or failure)
    if (!isProfileLoading) {
      clearTimeout(networkTimeoutId);
      const timer = setTimeout(() => {
        setIsLoading(false);
        // Hide splash screen when app is ready
        if (Capacitor.isPluginAvailable('SplashScreen')) {
          SplashScreen.hide()
            .catch(err => console.error('Error hiding splash screen:', err));
        }
      }, 500);
      return () => clearTimeout(timer);
    }

    return () => clearTimeout(networkTimeoutId);
  }, [isProfileLoading, initAttempts]);

  // Function to retry initialization after error
  const retryInitialization = () => {
    setInitError(null);
    setInitAttempts(prev => prev + 1);
    setIsLoading(true);
    refreshProfile();
  };

  // Show error screen if there's an initialization error
  if (initError) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-white p-4">
        <div className="text-center max-w-md">
          <h2 className="text-xl font-semibold text-red-600 mb-4">{initError.message}</h2>
          <p className="text-gray-700 mb-6">{initError.details}</p>
          <div className="bg-gray-100 p-3 rounded-lg mb-6 text-xs text-left overflow-auto max-h-32">
            <code>Error occurred during app initialization. Server may be unavailable. Try later</code>
          </div>
          <button
            onClick={retryInitialization}
            className="bg-green-600 text-white font-semibold py-3 px-6 rounded-full shadow-md hover:bg-green-700 w-full"
          >
            Retry Connection
          </button>
        </div>
      </div>
    );
  }

  // Show loading screen while initializing
  if (isLoading) {
    return '';
  }

  // Handle closing the create post modal
  const handleCloseCreatePostModal = () => {
    setShowCreatePostModal(false);
    // Clear shared media after closing the modal
    shareIntentService.clearSharedMedia();
    setSharedMedia([]);
  };

  // Handle adding a new post to the feed
  const handleAddPostToFeed = (newPost: any) => {
    // You can dispatch an event or use a context to update the feed
    console.log('New post created from shared media:', newPost);
    // Emit the post created event for all feed components to listen
    postEventService.emit('postCreated', newPost);
    // Close the modal after posting
    setShowCreatePostModal(false);
    shareIntentService.clearSharedMedia();
    setSharedMedia([]);
  };

  return (
    <IonReactRouter>
      <DebugBanner />
      {/* Create Post Modal for shared media */}
      {authInitialized && isSignedIn && profile && (
        <>
          {showCreatePostModal && (
            <CreatePostModal
              isOpen={showCreatePostModal}
              onOpenChange={handleCloseCreatePostModal}
              addPostToFeed={handleAddPostToFeed}
              sharedMedia={sharedMedia}
            />
          )}
        </>
      )}
      <Switch>
        {/* Show loading if auth is not initialized yet */}
        {!authInitialized && <Route path="*" component={GolfLoading} />}

        {/* Public Routes */}
        {authInitialized && !isSignedIn && !isProfileLoading && (
          <>
            <Route exact path="/mobile" component={Mobile} />
            <Route exact path="/sign-in" component={CustomSignIn} />
            <Route exact path="/sign-up" component={CustomSignUp} />
            <Route exact path="/forgot-password" component={ForgotPassword} />
            <Route exact path="/" render={() => (Capacitor.isNativePlatform() ? <Mobile /> : <Landing />)} />
          </>
        )}

        {/* Restricted Routes for Signed-In Users Without Profile */}
        {authInitialized && isSignedIn && !profile && !isProfileLoading && (
          <>
            <Route exact path="/" render={() => (Capacitor.isNativePlatform() ? <Mobile /> : <Landing />)} />
            <Route path="/welcome" component={Welcome} />
            <Route path="/dashboard" render={() => <Redirect to="/welcome" />} />
          </>
        )}

        {/* Authenticated Routes for Signed-In Users With Profile */}
        {authInitialized && isSignedIn && profile && (
          <>
            <Route path="/welcome" render={() => <Redirect to="/dashboard" />} />
            <Route path="/game-result/:gameId" component={GameResultPage} />
            <Route
              path="/"
              render={() => (
                <div className="flex flex-col h-screen-safe">
                  <div className="flex flex-1 h-screen overflow-hidden">
                    {/* Desktop layout */}
                    <div className="hidden md:block">
                      <MainSidebar />
                    </div>

                    <div className="relative flex-1 h-full-safe overflow-auto">
                      <MobileNav />
                    </div>
                  </div>
                </div>
              )}
            />
          </>
        )}

        {isProfileLoading && <div></div>}
        <Route path="*" component={NotFound} />
      </Switch>
    </IonReactRouter>
  );
};

// Error fallback component that shows when an error occurs
const ErrorFallback = ({ error, resetError }: { error: unknown, resetError: () => void }) => {
  const errorMessage = error instanceof Error ? error.message : String(error);
  const isDev = process.env.NODE_ENV === 'development';
  const router = useIonRouter();

  const { t } = useTranslation();

  const goToHome = () => {
    router.push('/', 'root', 'replace');
  };

  return (
    <IonApp>
      <IonPage>
        <IonContent className="ion-padding">
          <IonGrid fixed>
            <IonRow
              className="ion-justify-content-center ion-align-items-center"
              style={{ height: '100vh' }} // Ensures the row takes the full height of the viewport
            >
              <IonCol
                size="12"
                sizeMd="8"
                sizeLg="6"
                className="ion-text-center"
              >
                <div className="error-container ion-padding">
                  {/* Center-aligned SVG */}
                  <img
                    className="size-80 centered-img" // Added custom class for alignment
                    src="/assets/images/bottle.svg"
                    alt=""
                  />
                  <h2 className="font-bold text-xl mb-2">{t('oops')}</h2>

                  <p className="mb-4">{t('something_went_wrong')}</p>

                  {isDev && (
                    <IonCard className="ion-margin-bottom">
                      <IonCardContent className="ion-text-start">
                        <pre className="error-details text-sm">{errorMessage}</pre>
                      </IonCardContent>
                    </IonCard>
                  )}

                  <div className="ion-margin-top">
                    {isDev && (
                      <IonButton
                        onClick={resetError}
                        fill="outline"
                        color="warning"
                        expand="block"
                        className="ion-margin-bottom"
                      >
                        <IonIcon icon={refreshOutline} slot="start" />
                        {t('retry')}
                      </IonButton>
                    )}

                    <IonButton
                      onClick={goToHome}
                      expand="block"
                      color="primary"
                    >
                      Retourner à l'accueil
                    </IonButton>
                  </div>
                </div>
              </IonCol>
            </IonRow>
          </IonGrid>
        </IonContent>
      </IonPage>
    </IonApp>
  );
};

const App: React.FC = () => {
  return (
    <Sentry.ErrorBoundary fallback={ErrorFallback}>
      <IonApp>
        <IonReactRouter>
          <DebugModeProvider>
            <UserProfileProvider>
              <NotificationProvider>
                <DeepLinkProvider>
                  <AuthenticatedApp />
                </DeepLinkProvider>
              </NotificationProvider>
            </UserProfileProvider>
          </DebugModeProvider>
        </IonReactRouter>
      </IonApp>
    </Sentry.ErrorBoundary>
  );
};

export default App;